import { ChangeEvent, useState } from 'react'
import { Box, Grid, InputLabel } from '@mui/material'
import classNames from 'classnames'
import { useField, useFormikContext } from 'formik'
import { t } from 'i18next'

import { InputField } from 'src/components/shared/ui/form/fields'
import FieldsetGroup from 'src/components/shared/ui/form/fields/FieldsetGroup/FieldsetGroup'
import { fieldsStyles } from 'src/components/shared/ui/form/fields/fieldsStyles'
import { EscritosFormValues } from 'src/components/views/EscritosLexnet/components/shared/types/escritosFormTypes'
import { useTipoMensajeContext } from 'src/contexts/TipoMensajeContext'
import { CautelarOpciones, TipoMensaje } from 'src/enums/EscritosLegales'
import { FormField } from 'src/enums/Form'
import { NIGFieldStyles as styles } from './NIGViewStyles'

type Props = {
  changeFocus: (e: ChangeEvent<HTMLInputElement>) => void
}

function NIGView({ changeFocus }: Props): JSX.Element {
  const { values, setFieldTouched } = useFormikContext<EscritosFormValues>()

  const { tipoMensaje } = useTipoMensajeContext()
  const organoOrigenDescripcion = values[FormField.ORGANO_ORIGEN_DESCRIPCION]
  const cautelar = values[FormField.CAUTELAR]
  const metaNIG = useField(FormField.NIG_FULL_NUMBER)[1]

  const [active, setActive] = useState(false)

  function handleGroupBlur() {
    setActive(false)
    // El salto automático entre las 5 subcajas también dispara blur/focus
    // dentro del propio grupo, así que se difiere la comprobación para
    // validar solo cuando el foco sale realmente de todo el campo NIG.
    setTimeout(() => {
      const NIGContainer = document.querySelector('.NIG')
      const stillInsideGroup = NIGContainer?.contains(document.activeElement)
      if (!stillInsideGroup) {
        setFieldTouched(FormField.NIG_FULL_NUMBER, true, true)
      }
    }, 0)
  }

  const props = {
    clearable: false,
    spaceLabel: false,
    spaceError: false,
    sx: styles.input,
    onFocus: () => setActive(true),
    onBlur: handleGroupBlur,
    onChange: changeFocus
  }

  function isError() {
    return Boolean(metaNIG?.touched && metaNIG?.error && metaNIG?.error !== '')
  }

  function isRequiredNIG() {
    return (
      Boolean(
        organoOrigenDescripcion &&
          organoOrigenDescripcion.length &&
          tipoMensaje !== TipoMensaje.PERSONACION
      ) ||
      tipoMensaje === TipoMensaje.INICIADOR_EJECUCION ||
      (tipoMensaje === TipoMensaje.INICIADOR_ASUNTO && cautelar === CautelarOpciones.SI)
    )
  }

  const inputClassNames = classNames({
    disabled: false,
    required: isRequiredNIG(),
    error: isError(),
    clearable: false
  })

  return (
    <FieldsetGroup legend={t('form.legend.NIG')} isSecondaryType>
      <Box sx={styles.NIGFullNumberhiddenField}>
        <InputField id={FormField.NIG_FULL_NUMBER} />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={8} lg={8} xl={4}>
          <Box
            sx={styles.wrapper}
            data-error={isError()}
            data-active={active}
            data-disabled="false"
            className="multiField NIG"
          >
            <InputLabel sx={fieldsStyles.inputLabel} className={inputClassNames}>
              {t('form.field.NIG')}
            </InputLabel>

            <Box display="flex" flexDirection="row" sx={styles.composeInput}>
              <InputField
                id={FormField.NIG_1}
                maxLength={5}
                dataAttribute="1"
                required={isRequiredNIG()}
                {...props}
              />
              <Box component="span" sx={styles.separator} />
              <InputField id={FormField.NIG_2} maxLength={2} dataAttribute="2" {...props} />
              <Box component="span" sx={styles.separator} />
              <InputField id={FormField.NIG_3} maxLength={1} dataAttribute="3" {...props} />
              <Box component="span" sx={styles.separator} />
              <InputField id={FormField.NIG_4} maxLength={4} dataAttribute="4" {...props} />
              <Box component="span" sx={styles.separator} />
              <InputField id={FormField.NIG_5} maxLength={7} dataAttribute="5" {...props} />
            </Box>
            <Box component="span" sx={fieldsStyles.helpText}>
              {metaNIG.touched ? metaNIG.error : null}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </FieldsetGroup>
  )
}

export default NIGView
